import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import CountUp from "react-countup";
import {
  ShoppingCart,
  DollarSign,
  TrendingUp,
  CreditCard,
  FileText,
  ArrowRight,
  Plus,
  Users,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Calendar,
  PieChart,
  XCircle,
} from "lucide-react";
import Chart from "react-apexcharts";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "../../core/components/RecordNotFound";
import LoadingWrapper from "../../core/components/LoadingWrapper";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { all_routes } from "../../Router/all_routes";

const getMonthLabels = () => [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DashboardSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-32 rounded-xl" />
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 lg:grid-rows-2 gap-6 h-auto lg:h-[900px]">
      {/* Main Chart Skeleton */}
      <Skeleton className="lg:col-span-2 xl:col-span-3 xl:row-span-1 h-[450px] rounded-xl" />
      {/* Quick Actions Skeleton */}
      <Skeleton className="lg:col-span-1 xl:col-span-1 xl:row-span-1 h-[450px] rounded-xl" />
      {/* Payment Distribution Skeleton */}
      <Skeleton className="lg:col-span-1 xl:col-span-1 xl:row-span-1 h-[450px] rounded-xl" />
      {/* Table Skeleton */}
      <Skeleton className="lg:col-span-2 xl:col-span-3 xl:row-span-1 h-[450px] rounded-xl" />
    </div>
  </div>
);

const StatCard = ({
  title,
  value,
  icon: Icon,
  colorClass,
  trend,
  delay,
  currencySymbol,
}) => (
  <Card
    className={`animate-in fade-in slide-in-from-bottom-4 duration-700 delay-${delay} overflow-hidden relative`}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorClass} bg-opacity-10`}
        >
          <Icon className={`h-5 w-5 text-white`} />
        </div>
        {trend !== undefined && (
          <Badge
            variant="outline"
            className={`${trend >= 0 ? "text-green-600 border-green-200 bg-green-50" : "text-red-600 border-red-200 bg-red-50"}`}
          >
            {trend >= 0 ? (
              <ArrowUpRight className="h-3 w-3 mr-1" />
            ) : (
              <ArrowDownRight className="h-3 w-3 mr-1" />
            )}
            {Math.abs(trend).toFixed(1)}%
          </Badge>
        )}
      </div>
      <div>
        <h3 className="text-2xl font-black tracking-tight mb-1">
          <CountUp
            start={0}
            end={value || 0}
            duration={2.5}
            prefix={currencySymbol}
            separator=","
            decimals={2}
          />
        </h3>
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
      </div>
    </CardContent>
  </Card>
);

const QuickActionButton = (
  { icon: Icon, label, color, onClick },
) => (
  <Button
    variant="outline"
    className="relative h-auto flex-col gap-2 py-3 px-2 xl:py-5 xl:px-3 border rounded-xl bg-card border-border hover:border-primary/40 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-primary/5 transition-all duration-300 group overflow-hidden"
    onClick={onClick}
  >
    <div
      className={`absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-300 ${color}`}
    ></div>
    <div
      className={`relative z-10 h-8 w-8 xl:h-10 xl:w-10 shrink-0 rounded-2xl shadow-sm flex items-center justify-center transition-transform group-hover:scale-110 ${color}`}
    >
      <Icon className="h-4 w-4 xl:h-5 xl:w-5 text-white" />
    </div>
    <span className="relative z-10 font-bold text-[10px] xl:text-xs uppercase tracking-wide text-muted-foreground group-hover:text-foreground transition-colors leading-tight text-center">
      {label}
    </span>
  </Button>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "$";

  const token = useSelector((state) => state.bearer_token);
  const currentStore = useSelector((state) => state.current_store);

  const userData = useSelector((state) => state.user_data);
  const fullName =
    userData?.employee?.full_name || userData?.username || "User";

  const [dateFilter, setDateFilter] = useState("30 Days");

  const periodMap = {
    Today: "today",
    "7 Days": "7_days",
    "30 Days": "30_days",
    "1 Year": "1_year",
    "All Time": "all_time",
  };

  // Queries
  const {
    data: dashboardData,
    isLoading: isFirstLoad,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["dashboard", currentStore?.id, dateFilter],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.INIT, {
        params: {
          period: periodMap[dateFilter] || "30_days",
        },
      });
      return response.data.data;
    },
    enabled: !!token && !!currentStore?.id && hasPermission("dashboard view"),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Prevent refreshing on tab switch
  });

  // Data Processing
  const { recentTransactions, stats, salesData, purchaseData, chartLabels } =
    useMemo(() => {
      if (!dashboardData) {
        return {
          recentTransactions: [],
          stats: {
            totalPurchaseDue: 0,
            totalSalesDue: 0,
            totalSaleAmount: 0,
            totalExpenseAmount: 0,
            salesTrend: 0,
          },
          salesData: [],
          purchaseData: [],
          chartLabels: getMonthLabels(),
        };
      }

      const { summary, financial_overview, recent_activity } = dashboardData;

      return {
        recentTransactions: recent_activity || [],
        stats: {
          totalPurchaseDue: summary?.purchase_due || 0,
          totalSalesDue: summary?.outstanding_sales || 0,
          totalSaleAmount: summary?.total_sales_revenue || 0,
          totalExpenseAmount: summary?.total_expenses || 0,
          salesTrend: summary?.sales_growth_percentage || 0,
        },
        salesData:
          financial_overview?.sales?.map((v) => parseFloat(v || 0)) || [],
        purchaseData:
          financial_overview?.purchases?.map((v) => parseFloat(v || 0)) || [],
        chartLabels: financial_overview?.labels || getMonthLabels(),
      };
    }, [dashboardData]);

  const isDataLoading = isFirstLoad && !dashboardData;

  // Process Payment Methods for Pie Chart
  const paymentMethodsData = useMemo(() => {
    if (!dashboardData?.payment_distribution) {
      return { labels: [], series: [] };
    }

    const labels = dashboardData.payment_distribution.map((d) => d.method);
    const series = dashboardData.payment_distribution.map((d) =>
      parseFloat(d.amount || 0),
    );

    return {
      labels,
      series,
    };
  }, [dashboardData]);

  const pieChartOptions = useMemo(
    () => ({
      labels: paymentMethodsData.labels,
      colors: [
        "#2563ea",
        "#ef4444",
        "#f59e0b",
        "#10b981",
        "#8b5cf6",
        "#64748b",
      ],
      chart: {
        type: "donut",
        fontFamily: "Inter, sans-serif",
        toolbar: { show: false },
      },
      legend: { position: "bottom", markers: { radius: 12 } },
      noData: {
        text: "No Payment Data",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#64748b",
          fontSize: "14px",
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                showAlways: true,
                show: true,
                label: "Total Methods",
                fontSize: "12px",
                fontFamily: "Inter, sans-serif",
                color: "#64748b",
                formatter: () => paymentMethodsData.labels.length,
              },
              value: {
                fontSize: "18px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                offsetY: 2,
                formatter: (val) =>
                  currencySymbol +
                  parseFloat(val).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }),
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
    }),
    [paymentMethodsData.labels, currencySymbol],
  );

  const chartOptions = useMemo(
    () => ({
      colors: ["#2563ea", "#ef4444"],
      chart: {
        type: "bar",
        height: 350,
        stacked: false,
        toolbar: { show: false },
        fontFamily: "Inter, sans-serif",
      },
      plotOptions: {
        bar: { horizontal: false, borderRadius: 4, columnWidth: "45%" },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      xaxis: {
        categories: chartLabels,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: "#64748b", fontSize: "12px" } },
      },
      yaxis: {
        labels: {
          style: { colors: "#64748b", fontSize: "12px" },
          formatter: (val) => currencySymbol + Math.abs(val).toFixed(0),
        },
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,
        yaxis: { lines: { show: true } },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        offsetY: 0,
        itemMargin: { horizontal: 10, vertical: 0 },
        markers: { radius: 12 },
      },
      tooltip: {
        y: { formatter: (val) => currencySymbol + Math.abs(val).toFixed(2) },
      },
    }),
    [currencySymbol, chartLabels],
  );

  const chartSeries = useMemo(
    () => [
      { name: "Sales", data: salesData },
      { name: "Purchase", data: purchaseData },
    ],
    [salesData, purchaseData],
  );

  if (!hasPermission("dashboard view")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader className="mb-8 font-sans">
        <PageHeaderHeading
          title={`Welcome back, ${fullName.split(" ")[0]}`}
          subtitle={
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(new Date())}
            </span>
          }
        />
        <PageHeaderActions>
          <Tabs
            value={dateFilter}
            onValueChange={setDateFilter}
            className="w-full md:w-auto"
          >
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 md:w-auto h-auto p-1 bg-muted/50 rounded-lg border border-border/50">
              <TabsTrigger
                value="Today"
                className="px-4 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Today
              </TabsTrigger>
              <TabsTrigger
                value="7 Days"
                className="px-4 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                7 Days
              </TabsTrigger>
              <TabsTrigger
                value="30 Days"
                className="px-4 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                30 Days
              </TabsTrigger>
              <TabsTrigger
                value="1 Year"
                className="px-4 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground lg:inline-flex hidden"
              >
                1 Year
              </TabsTrigger>
              <TabsTrigger
                value="All Time"
                className="px-4 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground lg:inline-flex hidden"
              >
                All Time
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </PageHeaderActions>
      </PageHeader>

      <LoadingWrapper
        isLoading={isDataLoading}
        error={error}
        onRetry={refetch}
        skeleton={<DashboardSkeleton />}
      >
        <div
          className={`transition-opacity duration-300 ${isFetching ? "opacity-50 pointer-events-none" : "opacity-100"}`}
        >
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Sales Revenue"
              value={stats.totalSaleAmount}
              icon={TrendingUp}
              colorClass="bg-blue-600"
              trend={stats.salesTrend}
              delay={100}
              currencySymbol={currencySymbol}
            />
            <StatCard
              title="Outstanding Sales"
              value={stats.totalSalesDue}
              icon={DollarSign}
              colorClass="bg-amber-500"
              delay={200}
              currencySymbol={currencySymbol}
            />
            <StatCard
              title="Purchase Due"
              value={stats.totalPurchaseDue}
              icon={ShoppingCart}
              colorClass="bg-rose-500"
              delay={300}
              currencySymbol={currencySymbol}
            />
            <StatCard
              title="Total Expenses"
              value={stats.totalExpenseAmount}
              icon={CreditCard}
              colorClass="bg-violet-500"
              delay={400}
              currencySymbol={currencySymbol}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {/* Row 1: Financial Overview (Full on iPad, 75% on Desktop) */}
            <Card className="md:col-span-2 xl:col-span-3 xl:col-start-1 xl:row-start-1 shadow-sm border-0 ring-1 ring-slate-200 dark:ring-slate-800 h-[450px] flex flex-col">
              <CardHeader>
                <CardTitle>Financial Overview</CardTitle>
                <CardDescription>
                  Comparative analysis of sales vs purchases
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 min-h-0">
                <div className="w-full h-full">
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={350}
                    width="100%"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Row 2: Quick Actions (Left on iPad, Top-Right on Desktop) */}
            <Card className="md:col-span-1 xl:col-span-1 xl:col-start-4 xl:row-start-1 shadow-sm border-0 ring-1 ring-slate-200 dark:ring-slate-800 h-[450px] flex flex-col">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Commonly used operations</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 grid grid-cols-2 gap-2 xl:gap-3 content-center">
                <QuickActionButton
                  icon={ShoppingCart}
                  label="New Sale"
                  color="bg-blue-600"
                  onClick={() => navigate(all_routes.pos)}
                />
                <QuickActionButton
                  icon={Package}
                  label="Add Product"
                  color="bg-emerald-600"
                  onClick={() => navigate(all_routes.addproduct)}
                />
                <QuickActionButton
                  icon={Users}
                  label="Add Customer"
                  color="bg-violet-600"
                  onClick={() => navigate(all_routes.customers)}
                />
                <QuickActionButton
                  icon={FileText}
                  label="Add Expense"
                  color="bg-amber-600"
                  onClick={() => navigate(all_routes.expenses)}
                />
              </CardContent>
            </Card>

            {/* Row 2: Payment Channels (Right on iPad, Bottom-Right on Desktop) */}
            <Card className="md:col-span-1 xl:col-span-1 xl:col-start-4 xl:row-start-2 shadow-sm border-0 ring-1 ring-slate-200 dark:ring-slate-800 h-[450px] flex flex-col">
              <CardHeader>
                <CardTitle>Payment Channels</CardTitle>
                <CardDescription>Distribution by method</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 flex items-center justify-center">
                {paymentMethodsData.series.length > 0 ? (
                  <div className="w-full">
                    <Chart
                      options={pieChartOptions}
                      series={paymentMethodsData.series}
                      type="donut"
                      height={300}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-muted-foreground opacity-50">
                    <PieChart className="h-12 w-12 mb-2" />
                    <p>No payment data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Row 3: Recent Activity (Full on iPad, Bottom-Left on Desktop) */}
            <Card className="md:col-span-2 xl:col-span-3 xl:col-start-1 xl:row-start-2 shadow-sm border-0 ring-1 ring-slate-200 dark:ring-slate-800 h-[450px] flex flex-col animate-in fade-in slide-in-from-bottom-8 duration-700 delay-500">
              <CardHeader className="flex flex-row items-center justify-between shrink-0">
                <div>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Latest transactions and updates
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  className="text-sm text-primary"
                  onClick={() => navigate(all_routes.saleslist)}
                >
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="flex-1 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentTransactions.length > 0 ? (
                      recentTransactions.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium text-primary">
                            {item.transaction_id}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {item.customer || "Walk-In Customer"}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(item.date)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {currencySymbol}
                            {parseFloat(item.amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge
                              variant={
                                item.status?.toLowerCase() === "completed" ||
                                  item.status?.toLowerCase() === "paid"
                                  ? "success"
                                  : item.status?.toLowerCase() === "pending"
                                    ? "warning"
                                    : "outline"
                              }
                            >
                              {item.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No recent activity found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      </LoadingWrapper>
    </PageContainer>
  );
};

export default Dashboard;
